/**
 * Meeting Rooms — dashboard broadcast + strict state machine:
 *
 *   scheduled ──host▶ live ──host▶ ended
 *          ▲
 *   pending_partner_response ──partner accept▶ scheduled
 *                            ──partner reject▶ rejected
 *
 * Once a room is `live`, verified committee members (host org OR
 * accepted partner org) click "Join" and drop straight into an
 * embedded Jitsi conference — no links, no passwords.
 */
import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays, Users, Video, Check, X, PlusCircle,
  StopCircle, Radio, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWebSocket } from "@/hooks/useWebSocket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import JitsiMeetingRoom from "./JitsiMeetingRoom";

const STATUS_STYLES = {
  scheduled:                { label: "Scheduled",   color: "text-sky-300" },
  live:                     { label: "Live now",    color: "text-emerald-300" },
  ended:                    { label: "Ended",       color: "text-white/45" },
  rejected:                 { label: "Rejected",    color: "text-rose-300" },
  pending_partner_response: { label: "Awaiting invited orgs", color: "text-amber-300" },
};

const emptyRoom = {
  kind: "internal",
  title: "",
  scheduled_at: "",
  duration_min: 60,
  agenda: [{ title: "", duration_min: 15 }],
  partner_org_ids: [],
};

export default function MeetingRoomsPanel({ org, accent = "#4EC1E4" }) {
  const [rooms, setRooms] = useState([]);
  const [partners, setPartners] = useState([]);   // ACTIVE orgs for cross-assoc dropdown
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyRoom);
  const [saving, setSaving] = useState(false);
  const [activeJoin, setActiveJoin] = useState(null);   // Jitsi payload

  const refresh = async () => {
    try {
      const { data } = await api.get("/meeting-rooms");
      setRooms(data.items || []);
    } catch (e) {
      console.warn("[MeetingRoomsPanel] load skipped:", e?.message);
    }
  };

  useEffect(() => { void refresh(); }, [org?.id]);

  // Real-time: refetch whenever the backend broadcasts any meeting-*
  // event on the org channel this user is subscribed to.
  useWebSocket((evt) => {
    if (!evt?.type) return;
    if (evt.type.startsWith("meeting.")) void refresh();
  });

  useEffect(() => {
    if (!showCreate || form.kind !== "cross_association") return;
    api.get("/federation/associations", {
      params: { lifecycle: "ACTIVE", limit: 50 },
    }).then(({ data }) => {
      setPartners((data.items || []).filter((p) => p.id !== org?.id));
    }).catch(() => setPartners([]));
  }, [showCreate, form.kind, org?.id]);

  const create = async () => {
    if (!form.title.trim() || !form.scheduled_at) {
      return toast.error("Title and time are required");
    }
    if (form.kind === "cross_association" && form.partner_org_ids.length === 0) {
      return toast.error("Pick at least one invited association");
    }
    setSaving(true);
    try {
      await api.post("/meeting-rooms", {
        ...form,
        agenda: form.agenda.filter((a) => a.title.trim()),
      });
      toast.success("Meeting created — broadcasting to dashboards");
      setShowCreate(false);
      setForm(emptyRoom);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const respond = async (room, decision) => {
    try {
      await api.post(`/meeting-rooms/${room.id}/respond`, { decision });
      toast.success(decision === "accept" ? "Meeting accepted" : "Meeting rejected");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const start = async (room) => {
    try {
      const { data } = await api.post(`/meeting-rooms/${room.id}/start`);
      toast.success("Meeting is live — dropping into video room");
      await refresh();
      // Auto-drop the host into the video conf.
      await join({ ...room, ...data.meeting_room });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    }
  };

  const endMeeting = async (room) => {
    try {
      await api.post(`/meeting-rooms/${room.id}/end`);
      toast.success("Meeting ended");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const join = async (room) => {
    try {
      const { data } = await api.get(`/meeting-rooms/${room.id}/join`);
      setActiveJoin({ ...data, title: room.title });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    }
  };

  const setAgenda = (idx, patch) => {
    setForm((f) => ({
      ...f,
      agenda: f.agenda.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  };

  const partnersLabel = useMemo(() => (roomIds) => {
    if (!roomIds || roomIds.length === 0) return "";
    return roomIds
      .map((pid) => partners.find((p) => p.id === pid)?.brand_name || pid.slice(0, 6))
      .join(", ");
  }, [partners]);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="meeting-rooms-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
            Federation Meeting Rooms
          </div>
          <h2 className="font-display text-xl uppercase tracking-tight">
            {rooms.length} scheduled · video-enabled
          </h2>
        </div>
        <Button onClick={() => setShowCreate(true)}
                className="h-9 gap-1.5"
                style={{ background: accent, color: "#0b0b0b" }}
                data-testid="meeting-create-btn">
          <PlusCircle size={14}/> New room
        </Button>
      </div>

      {rooms.length === 0 ? (
        <div className="text-sm text-white/55 py-8 text-center">
          No meetings scheduled. Create one to broadcast an invite to
          partner associations.
        </div>
      ) : (
        <div className="space-y-3" data-testid="meeting-rooms-list">
          {rooms.map((r) => {
            const style = STATUS_STYLES[r.status] || STATUS_STYLES.scheduled;
            const needsResponse = !r.is_host && r.my_response === "pending";
            const canStart = r.is_host && r.status === "scheduled";
            const isLive = r.status === "live";
            return (
              <div key={r.id}
                   className="rounded-xl border border-white/10 bg-black/30 p-4"
                   data-testid={`meeting-row-${r.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] tracking-[0.22em] uppercase ${style.color}`}
                            data-testid={`meeting-status-${r.id}`}>
                        {style.label}
                      </span>
                      {isLive && (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-emerald-300">
                          <Radio size={10} className="animate-pulse"/> On air
                        </span>
                      )}
                      <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">
                        {r.kind.replace("_", " ")}
                      </span>
                    </div>
                    <div className="font-display text-lg mt-1"
                         data-testid={`meeting-title-${r.id}`}>
                      {r.title}
                    </div>
                    <div className="text-[11px] text-white/55 flex items-center gap-3 flex-wrap mt-1">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays size={11}/>{" "}
                        {new Date(r.scheduled_at).toLocaleString(undefined, {
                          dateStyle: "medium", timeStyle: "short",
                        })}
                        {" · "}{r.duration_min} min
                      </span>
                      {(r.partner_org_ids?.length || 0) > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Users size={11}/> {r.partner_org_ids.length} partner org(s)
                        </span>
                      )}
                    </div>
                    {r.agenda?.length > 0 && (
                      <ul className="mt-2 text-[11px] text-white/60 space-y-0.5">
                        {r.agenda.slice(0, 3).map((a, i) => (
                          <li key={i}>· {a.title} <span className="text-white/35">({a.duration_min || 15} min)</span></li>
                        ))}
                        {r.agenda.length > 3 && (
                          <li className="text-white/35">+ {r.agenda.length - 3} more</li>
                        )}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {needsResponse && (
                      <>
                        <Button className="h-8 gap-1 bg-emerald-500 text-black hover:bg-emerald-400"
                                onClick={() => respond(r, "accept")}
                                data-testid={`meeting-accept-${r.id}`}>
                          <Check size={13}/> Accept
                        </Button>
                        <Button variant="ghost"
                                className="h-8 gap-1 text-rose-300 hover:text-rose-200"
                                onClick={() => respond(r, "reject")}
                                data-testid={`meeting-reject-${r.id}`}>
                          <X size={13}/> Reject
                        </Button>
                      </>
                    )}
                    {canStart && (
                      <Button className="h-8 gap-1 bg-emerald-500 text-black hover:bg-emerald-400"
                              onClick={() => start(r)}
                              data-testid={`meeting-start-${r.id}`}>
                        <Video size={13}/> Start
                      </Button>
                    )}
                    {r.can_join && (
                      <Button className="h-8 gap-1 bg-emerald-500 text-black hover:bg-emerald-400"
                              onClick={() => join(r)}
                              data-testid={`meeting-join-${r.id}`}>
                        <Video size={13}/> Join
                      </Button>
                    )}
                    {r.is_host && isLive && (
                      <Button variant="ghost"
                              className="h-8 gap-1 text-rose-300 hover:text-rose-200"
                              onClick={() => endMeeting(r)}
                              data-testid={`meeting-end-${r.id}`}>
                        <StopCircle size={13}/> End
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">
              New Meeting Room
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Room type</Label>
                <select value={form.kind}
                        onChange={(e) => setForm({ ...form, kind: e.target.value,
                                                   partner_org_ids: [] })}
                        className="mt-1 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-sm"
                        data-testid="meeting-kind">
                  <option value="internal">Internal (my org only)</option>
                  <option value="cross_association">Cross-association</option>
                  <option value="external">External invitees</option>
                </select>
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" min={15} max={480}
                       value={form.duration_min}
                       onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value || "60", 10) })}
                       className="mt-1 bg-black/40 border-white/15"
                       data-testid="meeting-duration"/>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="meeting-title-input"
                     placeholder="Q2 Executive Sync"/>
            </div>
            <div>
              <Label>Scheduled at (ISO)</Label>
              <Input type="datetime-local"
                     value={form.scheduled_at}
                     onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="meeting-scheduled-at"/>
            </div>

            {form.kind === "cross_association" && (
              <div>
                <Label>Invited associations</Label>
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-white/10 bg-black/40 p-2"
                     data-testid="meeting-partners-list">
                  {partners.length === 0 ? (
                    <div className="text-xs text-white/45 p-2">
                      No ACTIVE partner orgs yet.
                    </div>
                  ) : partners.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                      <input type="checkbox"
                             checked={form.partner_org_ids.includes(p.id)}
                             onChange={(e) => {
                               const on = e.target.checked;
                               setForm((f) => ({
                                 ...f,
                                 partner_org_ids: on
                                   ? [...f.partner_org_ids, p.id]
                                   : f.partner_org_ids.filter((x) => x !== p.id),
                               }));
                             }}/>
                      <span>{p.brand_name} <span className="text-white/40">· {p.association_type}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Agenda</Label>
                <Button type="button" variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => setForm((f) => ({
                          ...f,
                          agenda: [...f.agenda, { title: "", duration_min: 15 }],
                        }))}
                        data-testid="agenda-add">
                  + Add item
                </Button>
              </div>
              <div className="space-y-2">
                {form.agenda.map((a, i) => (
                  <div key={i} className="flex items-center gap-2"
                       data-testid={`agenda-row-${i}`}>
                    <Input value={a.title}
                           onChange={(e) => setAgenda(i, { title: e.target.value })}
                           className="flex-1 bg-black/40 border-white/15"
                           placeholder={`Item ${i + 1} title`}/>
                    <Input type="number" min={5} max={240}
                           value={a.duration_min}
                           onChange={(e) => setAgenda(i, { duration_min: parseInt(e.target.value || "15", 10) })}
                           className="w-20 bg-black/40 border-white/15"/>
                    <Button variant="ghost" className="h-9 px-2 text-white/45"
                            onClick={() => setForm((f) => ({
                              ...f,
                              agenda: f.agenda.filter((_, j) => j !== i),
                            }))}>
                      <Trash2 size={13}/>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving}
                    className="bg-white text-black hover:bg-white/90"
                    data-testid="meeting-submit-btn">
              {saving ? "Creating…" : "Broadcast invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live video overlay */}
      {activeJoin && (
        <JitsiMeetingRoom join={activeJoin} onClose={() => setActiveJoin(null)}/>
      )}
    </div>
  );
}
